// frontend/src/pages/WheelConfigPage.js
//
// Super-admin screen to manage the scoring wagon wheel's custom shots and
// position chips WITHOUT a code deploy. Reads/writes GET|PUT /scorer/wheel-config.
// Built-in shots/positions always remain; this only adds extras on top.

import React, { useEffect, useState } from 'react';
import api from '../utils/api';

const ZONES = ['Straight', 'Mid-Off', 'Cover', 'Point', 'Third Man', 'Fine Leg', 'Square Leg', 'Midwicket', 'Mid-On'];
const RINGS = [
  { v: 'inner',    label: 'Close-in (inner)' },
  { v: 'circle',   label: 'In the ring (circle)' },
  { v: 'boundary', label: 'Deep (boundary)' },
];
const BUILT_IN_SHOTS = ['Drive', 'Cut', 'Pull', 'Sweep', 'Glance', 'Flick', 'Defence', 'Lofted', 'Edge',
  'Reverse Sweep', 'Slog', 'Ramp/Scoop', 'Late Cut', 'Square Drive', 'Punch', 'Backfoot Punch', 'Frontfoot Punch'];

const slug = (s) => String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const card  = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 };
const h2    = { fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#1E3A6E', margin: '0 0 4px' };
const sub   = { fontSize: '0.8rem', color: '#64748b', margin: '0 0 12px' };
const input = { padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const btn   = { border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontFamily: 'Rajdhani,sans-serif', cursor: 'pointer', fontSize: '0.9rem' };
const chip  = (on) => ({ border: '1px solid', borderColor: on ? '#1E3A6E' : '#cbd5e1', background: on ? '#1E3A6E' : '#fff', color: on ? '#fff' : '#475569', borderRadius: 999, padding: '4px 10px', fontSize: '0.78rem', cursor: 'pointer' });

export default function WheelConfigPage() {
  const [shots, setShots] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // New-shot form
  const [sLabel, setSLabel] = useState('');
  const [sVerb, setSVerb] = useState('');
  const [sSectors, setSSectors] = useState([]);
  // New-position form
  const [pSector, setPSector] = useState(0);
  const [pRing, setPRing] = useState('boundary');
  const [pLabel, setPLabel] = useState('');

  useEffect(() => {
    api.get('/scorer/wheel-config')
      .then(res => {
        setShots(Array.isArray(res.data?.shots) ? res.data.shots : []);
        setPositions(Array.isArray(res.data?.positions) ? res.data.positions : []);
      })
      .catch(() => setMsg({ type: 'err', text: 'Could not load config.' }))
      .finally(() => setLoading(false));
  }, []);

  function toggleSector(i) {
    setSSectors(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i].sort((a, b) => a - b));
  }

  function addShot() {
    const label = sLabel.trim();
    if (!label) return;
    const key = slug(label);
    if (!key) return;
    if (BUILT_IN_SHOTS.map(slug).includes(key) || shots.some(s => s.key === key)) {
      setMsg({ type: 'err', text: `"${label}" already exists.` });
      return;
    }
    setShots(prev => [...prev, { key, label, verb: (sVerb.trim() || label).toLowerCase(), sectors: sSectors }]);
    setSLabel(''); setSVerb(''); setSSectors([]); setMsg(null);
  }

  function addPosition() {
    const label = pLabel.trim();
    if (!label) return;
    setPositions(prev => [...prev, { sector: Number(pSector), ring: pRing, label, sideHint: slug(label) }]);
    setPLabel(''); setMsg(null);
  }

  const removeShot = (key) => setShots(prev => prev.filter(s => s.key !== key));
  const removePosition = (idx) => setPositions(prev => prev.filter((_, i) => i !== idx));

  async function save() {
    setSaving(true); setMsg(null);
    try {
      await api.put('/scorer/wheel-config', { shots, positions });
      setMsg({ type: 'ok', text: 'Saved. Scorers will see the changes after a refresh.' });
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.error || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div style={{ padding: 24, color: '#64748b' }}>Loading wheel setup…</div>;

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 14px 60px' }}>
      <h1 style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, color: '#1E3A6E', fontSize: '1.5rem', marginBottom: 4 }}>
        🏏 Wagon Wheel Setup
      </h1>
      <p style={{ fontSize: '0.82rem', color: '#64748b', marginBottom: 16 }}>
        Add your own shot types and fielding positions to the live scoring wheel.
        Built-in ones are always available — this only adds extras on top.
      </p>

      {msg && (
        <div style={{ ...card, marginBottom: 16, background: msg.type === 'ok' ? '#f0fdf4' : '#fef2f2',
          borderColor: msg.type === 'ok' ? '#86efac' : '#fecaca', color: msg.type === 'ok' ? '#166534' : '#b91c1c', fontSize: '0.85rem' }}>
          {msg.text}
        </div>
      )}

      {/* ── SHOTS ── */}
      <div style={card}>
        <div style={h2}>Shot types</div>
        <div style={sub}>The stroke played (e.g. "Square Cut"). Pick which wheel zones it should show as a default chip — it's always available under "+ other" too.</div>

        {shots.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {shots.map(s => (
              <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, color: '#1E3A6E' }}>{s.label}</span>
                  <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}> · "{s.verb}"</span>
                  {Array.isArray(s.sectors) && s.sectors.length > 0 && (
                    <span style={{ color: '#64748b', fontSize: '0.75rem' }}> · {s.sectors.map(i => ZONES[i]).join(', ')}</span>
                  )}
                </div>
                <button style={{ ...btn, background: '#fef2f2', color: '#b91c1c', padding: '5px 10px' }} onClick={() => removeShot(s.key)}>Remove</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>Shot name</label>
            <input style={input} value={sLabel} onChange={e => setSLabel(e.target.value)} placeholder="e.g. Square Cut" />
          </div>
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>Commentary word <span style={{ fontWeight: 400 }}>(optional)</span></label>
            <input style={input} value={sVerb} onChange={e => setSVerb(e.target.value)} placeholder='e.g. "square-cuts"' />
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: 6 }}>Show as default chip on zones</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ZONES.map((z, i) => (
              <button key={i} style={chip(sSectors.includes(i))} onClick={() => toggleSector(i)}>{z}</button>
            ))}
          </div>
        </div>
        <button style={{ ...btn, background: '#1E3A6E', color: '#fff' }} onClick={addShot}>+ Add shot</button>

        <div style={{ marginTop: 14, fontSize: '0.72rem', color: '#94a3b8' }}>
          Built-in (always available): {BUILT_IN_SHOTS.join(', ')}
        </div>
      </div>

      {/* ── POSITIONS ── */}
      <div style={card}>
        <div style={h2}>Position chips</div>
        <div style={sub}>Extra named fielding positions for the POSITION row (e.g. "Sweeper Cover" at Cover, deep).</div>

        {positions.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {positions.map((p, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 700, color: '#1E3A6E' }}>{p.label}</span>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}> · {ZONES[p.sector]} · {(RINGS.find(r => r.v === p.ring) || {}).label || p.ring}</span>
                </div>
                <button style={{ ...btn, background: '#fef2f2', color: '#b91c1c', padding: '5px 10px' }} onClick={() => removePosition(idx)}>Remove</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>Zone</label>
            <select style={input} value={pSector} onChange={e => setPSector(Number(e.target.value))}>
              {ZONES.map((z, i) => <option key={i} value={i}>{z}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>Depth</label>
            <select style={input} value={pRing} onChange={e => setPRing(e.target.value)}>
              {RINGS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b' }}>Position name</label>
          <input style={input} value={pLabel} onChange={e => setPLabel(e.target.value)} placeholder="e.g. Sweeper Cover" />
        </div>
        <button style={{ ...btn, background: '#1E3A6E', color: '#fff' }} onClick={addPosition}>+ Add position</button>
      </div>

      <button
        style={{ ...btn, background: saving ? '#94a3b8' : '#16a34a', color: '#fff', width: '100%', padding: '12px', fontSize: '1rem' }}
        disabled={saving}
        onClick={save}
      >
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}
